import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useToken } from '@app/hooks';
import { useMyProfile } from '@app/hooks/user/myprofile';
import { VITE_APP_API_MEDIA_URL } from '@/app/constants/APIKEYS';
import { IMAGES } from '@/app/constants';
import CIUtils from '@/utils/ciutils';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Icons from local assets
const bagIconUrl = '/assets/newGNB/bag-icon.svg';
const secuIconUrl = '/assets/newGNB/secu-icon.svg';
const markIconUrl = '/assets/newGNB/mark-icon.svg';
const scanIconUrl = '/assets/newGNB/scan-icon.svg';
const starIconUrl = '/assets/newGNB/star-icon.svg';
const setIconUrl = '/assets/newGNB/set-icon.svg';
const lineIconUrl = '/assets/newGNB/line-icon.svg';

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const ProfileImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ff8a00;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DropdownBridge = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 328px;
  height: 12px;
  z-index: 1001;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #0d1117;
  border-radius: 12px;
  width: 328px;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  z-index: 1001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: ${(props) => (props.$isOpen ? 'auto' : 'none')};
`;

const UserInfoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  gap: 8px;
`;

const UserInfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const UserInfoRight = styled.div`
  display: flex;
  align-items: center;
`;

const LogoutButton = styled.button`
  border: 1px solid #8d8d8d;
  border-radius: 12px;
  padding: 8px 20px;
  background: transparent;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const UserName = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  line-height: normal;
  margin: 0;
  min-width: 0;
  word-break: break-word;
`;

const UserUID = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.24px;
  line-height: normal;
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: #353535;
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MenuIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const { data: token } = useToken();
  const { data: myinfo } = useMyProfile();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!token || !myinfo) {
    return null;
  }

  const profileImageUrl = myinfo?.profileimage
    ? `${VITE_APP_API_MEDIA_URL}/${myinfo.profileimage}`
    : IMAGES.avatar;

  const handleLogout = () => {
    // TODO: Implement logout logic
    navigate('/logout');
  };

  const menuItems = [
    { icon: bagIconUrl, text: t('assets') || 'Assets', path: '/asset' },
    // { icon: secuIconUrl, text: t('accountSecurity') || 'Account & Security', path: '/account' },
    {
      icon: markIconUrl,
      text: t('referralProgram') || 'Affiliate Program',
      path: '/affiliate-program',
    },
    {
      icon: scanIconUrl,
      text: t('audit') || 'Audit',
      path: '/account?tab=audit',
    },
    // { icon: starIconUrl, text: t('competitionHistory') || 'Competition History', path: '/trading-competition-user' },
    {
      icon: starIconUrl,
      text: t('competitionHistory') || 'Competition History',
      path: '/trade-competition/1',
    },
    {
      icon: setIconUrl,
      text: t('accountSecurity') || 'Settings',
      path: '/account',
    },
  ];

  return (
    <ProfileContainer
      ref={dropdownRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <ProfileImageWrapper>
        <ProfileImage src={profileImageUrl} alt="Profile" />
      </ProfileImageWrapper>
      {isOpen && <DropdownBridge />}
      <DropdownMenu $isOpen={isOpen}>
        <UserInfoSection>
          <UserInfoLeft>
            <ProfileImageWrapper style={{ width: '32px', height: '32px' }}>
              <ProfileImage src={profileImageUrl} alt="Profile" />
            </ProfileImageWrapper>
            <div>
              <UserName>{myinfo?.nickname || 'User'}</UserName>
              <UserUID>UID : {CIUtils.padId(myinfo?.id)}</UserUID>
            </div>
          </UserInfoLeft>
          <UserInfoRight>
            <LogoutButton onClick={handleLogout}>
              {t('logout') || 'Logout'}
            </LogoutButton>
          </UserInfoRight>
        </UserInfoSection>
        <Divider />
        <MenuList>
          {menuItems.map((item, index) => (
            <MenuItem
              key={index}
              to={item.path}
              onClick={() => setIsOpen(false)}
            >
              <MenuIcon>
                <img
                  src={item.icon}
                  alt={item.text}
                  style={{ width: '24px', height: '24px' }}
                />
              </MenuIcon>
              <MenuText>{item.text}</MenuText>
            </MenuItem>
          ))}
        </MenuList>
      </DropdownMenu>
    </ProfileContainer>
  );
};

export default UserProfile;
